/*
 * Image upload rules for the organizer upload dialog (mock event.html `UPS` /
 * `upCheck`). Pure: no DOM, no imports — the dialog decodes the bitmap and hands
 * the measured entry here, and turns the returned issue into a translated line.
 *
 * Banner keeps its 5 MB allowance (Figma copy); everything else is the 2 MB the
 * production API enforces. Partner logos are bounded from above because they are
 * displayed 46px tall. The event logo has no dimension gate: the tile is 154px
 * and object-contain, the API never enforced one, and the mock's 500 × 500 square
 * rule refused logos the legacy page accepts.
 */
export const EVENT_HUB_UPLOAD_KIND = {
    BANNER: 'banner',
    LOGO: 'logo',
    PARTNER: 'partner',
    PHOTOS: 'photos',
    PRODUCT: 'product',
}

const MB = 1024 * 1024

export const EVENT_HUB_UPLOAD_RULES = {
    [EVENT_HUB_UPLOAD_KIND.BANNER]: { maxBytes: 5 * MB, multiple: false, minWidth: 1920, minHeight: 640 },
    [EVENT_HUB_UPLOAD_KIND.LOGO]: { maxBytes: 2 * MB, multiple: false },
    [EVENT_HUB_UPLOAD_KIND.PARTNER]: { maxBytes: 2 * MB, multiple: false, maxWidth: 600, maxHeight: 200 },
    [EVENT_HUB_UPLOAD_KIND.PHOTOS]: { maxBytes: 2 * MB, multiple: true },
    // the product picture of the organizer ProductForm (mock `rsPicNote`): a 72 px thumbnail
    // scaled up on the public form, so at least 600 × 600
    [EVENT_HUB_UPLOAD_KIND.PRODUCT]: { maxBytes: 2 * MB, multiple: false, minWidth: 600, minHeight: 600 },
}

/** Only JPEG and PNG go through, whatever the extension says. */
export const isAcceptedUploadType = (mimeType) => /^image\/(png|jpeg)$/.test(mimeType || '')

/** "820 KB" / "2.4 MB" — unit labels are passed in so the caller can translate them. */
export const formatUploadBytes = (bytes, units = { kb: 'KB', mb: 'MB' }) => {
    const size = Number(bytes) || 0
    if (size < MB) {
        return `${Math.round(size / 1024)} ${units.kb}`
    }
    return `${(size / MB).toFixed(1).replace(/\.0$/, '')} ${units.mb}`
}

/** Issue codes returned by getUploadFileIssue; each maps to one translated line. */
export const UPLOAD_ISSUE = {
    TYPE: 'type',
    SIZE: 'size',
    MIN: 'min',
    MAX: 'max',
}

/**
 * First reason a picked file is refused under `rule`, or null when it passes.
 * `entry` is `{ type, size, decoded, width, height }` — `decoded` false means the
 * browser could not read the bitmap (wrong type or corrupt), which is a refusal.
 */
export const getUploadFileIssue = (entry, rule) => {
    if (!entry || !rule) {
        return null
    }
    if (!isAcceptedUploadType(entry.type) || !entry.decoded) {
        return { code: UPLOAD_ISSUE.TYPE }
    }
    if (entry.size > rule.maxBytes) {
        return { code: UPLOAD_ISSUE.SIZE, params: { size: entry.size, max: rule.maxBytes } }
    }
    const { width, height } = entry
    if ((rule.minWidth && width < rule.minWidth) || (rule.minHeight && height < rule.minHeight)) {
        return {
            code: UPLOAD_ISSUE.MIN,
            params: { width, height, minWidth: rule.minWidth, minHeight: rule.minHeight },
        }
    }
    if ((rule.maxWidth && width > rule.maxWidth) || (rule.maxHeight && height > rule.maxHeight)) {
        return {
            code: UPLOAD_ISSUE.MAX,
            params: { width, height, maxWidth: rule.maxWidth, maxHeight: rule.maxHeight },
        }
    }
    return null
}

/** Files the dialog may submit: the ones without an issue, capped to one unless the rule allows many. */
export const getValidUploadFiles = (entries, rule) => {
    const valid = (entries || []).filter((entry) => !entry.issue)
    return rule?.multiple ? valid : valid.slice(0, 1)
}
